// noinspection JSUnusedGlobalSymbols

import { createContext } from 'react'
import * as qs from 'qs'
import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

export type IGraphPayload<P> = P & { '@odata.context'?: string, '@odata.nextLink'?: string }
export type IGraphArrayPayload<P> = IGraphPayload<{ value: P[] }>
export type IGraphArrayResponse<P = any> = AxiosResponse<IGraphArrayPayload<P>>
export type IGraphResponse<P = any> = AxiosResponse<IGraphPayload<P>>

export enum Destination {
    Hammock = 'HAMMOCK',
    Exchange = 'EXCHANGE',
    ReplicationEmail = 'REPLICATION_EMAIL',
    Email = 'EMAIL',
    PrivateEmail = 'PRIVATE_EMAIL',
    SharePoint = 'SHAREPOINT',
    Documents = 'DOCUMENTS',
    Admin = 'ADMIN',
}

export type AxiosProps<P = {}> = P & { axios: AxiosInstance }

export type SpringBootError = {
    error: string
    message: string
    path: string
    status: number
    timestamp: string
    trace?: string
    cause?: { error?: { code: string; message?: { lang: string; value: string } } }
}

export type GraphError = {
    error: {
        code: string
        innerError: {
            'client-request-id': string
            date: string
            'request-id': string
        }
        message: string
    }
}

export type DataPromise<D = undefined, S extends DataPromiseSettings = undefined, E = SpringBootError> = {
    data: D
    loading: boolean
    loaded: boolean
    rejected?: boolean
    settings?: S
    error?: AxiosError<E>
}
export type DataPromiseSettings = {
    previousSettings?: DataPromiseSettings
} & { [key: string]: any }

export enum DataPromiseState {
    Loaded = 'loaded',
    Loading = 'loading',
    Rejected = 'rejected',
}

export const isSpringBootError = (error: AxiosError): error is AxiosError<SpringBootError> =>
    !!(error.response?.data as SpringBootError)?.message

export const isGraphError = (error: AxiosError): error is AxiosError<GraphError> =>
    !!(error.response?.data as GraphError)?.error?.message

export const graphBaseUrl = 'https://graph.microsoft.com/v1.0'
export const axiosConfig: AxiosRequestConfig = {
    timeout: 30000,
    paramsSerializer: (params: any) => qs.stringify(params, { arrayFormat: 'comma' }),
}

export function getDataPromiseState<D = undefined, S extends DataPromiseSettings = undefined, E = SpringBootError>(
    toggle: DataPromiseState,
    data?: D,
    settings?: S,
    error?: AxiosError<E>,
): DataPromise<D, S, E> {
    return {
        data,
        loaded: toggle === DataPromiseState.Loaded,
        loading: toggle === DataPromiseState.Loading,
        rejected: toggle === DataPromiseState.Rejected,
        settings,
        error,
    }
}

export function getDataEmptyState<D = undefined, S extends DataPromiseSettings = undefined, E = SpringBootError>(
    settings?: S,
    defaultData?: D,
): DataPromise<D, S, E> {
    return getDataPromiseState<D, S, E>(undefined, defaultData, settings)
}

export function getDataLoadingState<D = undefined, S extends DataPromiseSettings = undefined, E = SpringBootError>(
    settings?: S,
    previousData?: D,
): DataPromise<D, S, E> {
    return getDataPromiseState<D, S, E>(DataPromiseState.Loading, previousData, settings)
}

export function getDataLoadedState<D = undefined, S extends DataPromiseSettings = undefined, E = SpringBootError>(
    newData?: D,
    settings?: S,
): DataPromise<D, S, E> {
    return getDataPromiseState<D, S, E>(DataPromiseState.Loaded, newData, settings)
}

export function getDataRejectedState<D = undefined, S extends DataPromiseSettings = undefined, E = SpringBootError>(
    settings?: S,
    defaultData?: D,
    error?: AxiosError<E>,
): DataPromise<D, S, E> {
    return getDataPromiseState<D, S, E>(DataPromiseState.Rejected, defaultData, settings, error)
}

export function shouldDispatchLoad<D = undefined, S extends DataPromiseSettings = undefined, E = SpringBootError>(
    entity: DataPromise<D, S, E>,
): boolean {
    return !entity || (!entity.loading && !entity.loaded && !entity.rejected)
}

const AxiosContext = createContext<AxiosInstance>(null)

export default AxiosContext
